import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  Check,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  LockOutlined,
  RadioButtonUnchecked,
  Refresh,
  Save,
} from "@mui/icons-material";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import MediaUploadPanel from "./MediaUploadPanel";
import SecureMediaPreview from "./SecureMediaPreview";
import { mediaApi } from "./mediaApi";
import {
  buildMediaPayload,
  EDITORIAL_SECTIONS,
  emptyMediaForm,
  formatDuration,
  formatFileSize,
  getInitialMediaWorkflowStep,
  getPublicationReadiness,
  isAssetUploadActive,
  mediaToForm,
  richTextToPlainText,
  sanitizeRichTextForPreview,
  TRANSCRIPT_MAX_LENGTH,
  validateMediaField,
  validateMediaForm,
} from "./media.utils";

const WORKFLOW_STEPS = ["Content Details", "Upload Media", "Review & Publish"];

const listField = (label, field, values, setValues, error, helperText, onBlur) => (
  <Autocomplete
    multiple
    freeSolo
    options={[]}
    value={values}
    onChange={(event, next) => setValues(next)}
    onBlur={onBlur}
    renderTags={(items, getTagProps) =>
      items.map((item, index) => <Chip label={item} {...getTagProps({ index })} />)
    }
    renderInput={(params) => (
      <TextField {...params} label={label} error={Boolean(error)} helperText={error || helperText}
        inputProps={{ ...params.inputProps, "data-media-field-input": field }} />
    )}
  />
);

const readinessIcon = (state) => {
  if (state === "complete" || state === "ready") return <CheckCircle />;
  if (state === "processing") return <HourglassEmpty />;
  if (state === "error") return <ErrorOutline />;
  if (state === "locked") return <LockOutlined />;
  return <RadioButtonUnchecked />;
};

const Section = ({ title, description, children }) => (
  <section className="media-wizard-section">
    <Box className="media-wizard-section-heading">
      <Typography variant="h6" component="h2">{title}</Typography>
      {description && <Typography variant="body2" color="textSecondary">{description}</Typography>}
    </Box>
    {children}
  </section>
);

const ReviewValue = ({ label, children, fullWidth = false }) => (
  <Box className={`media-review-value${fullWidth ? " media-review-value--wide" : ""}`}>
    <Typography component="dt" variant="caption">{label}</Typography>
    <Typography component="dd" variant="body2">{children || "—"}</Typography>
  </Box>
);

const RichTextField = ({ compact = false, error, field, label, required = false, value, onBlur, onChange }) => {
  return (
    <Box data-media-field={field} onBlur={onBlur}
      className={`media-rich-text-field${error ? " media-rich-text-field--error" : ""}`}>
      <Typography component="label" variant="subtitle2">
        {label}{required ? " *" : ""}
      </Typography>
      <RichTextEditor
        compact={compact}
        content={value}
        label={`${label} rich text editor`}
        mediaMode
        setContent={onChange}
      />
      <FormHelperText error={Boolean(error)}>
        {error || (required ? "Required before publishing." : "Optional.")}
      </FormHelperText>
    </Box>
  );
};

const ReviewRichText = ({ label, value }) => (
  <Box className="media-review-value media-review-value--wide">
    <Typography component="dt" variant="caption">{label}</Typography>
    {richTextToPlainText(value) ? (
      <Box
        component="dd"
        className="media-rich-text-preview"
        dangerouslySetInnerHTML={{ __html: sanitizeRichTextForPreview(value) }}
      />
    ) : (
      <Typography component="dd" variant="body2">—</Typography>
    )}
  </Box>
);

const ReviewTranscript = ({ value }) => (
  <Box className="media-review-value media-review-value--wide">
    <Typography component="dt" variant="caption">Transcript</Typography>
    <Typography component="dd" variant="body2" className="media-transcript-preview">
      {value || "—"}
    </Typography>
  </Box>
);

const MediaEditorDialog = ({ open, kind, initialMedia, onClose, onSaved }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const initialMediaId = initialMedia?._id;
  const initialAssetStatus = initialMedia?.assetStatus;
  const [form, setForm] = useState(emptyMediaForm(kind));
  const [media, setMedia] = useState(initialMedia || null);
  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [, setTouched] = useState({});
  const [message, setMessage] = useState("");
  const [messageSeverity, setMessageSeverity] = useState("info");
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const savingRef = useRef(false);
  const actingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const firstStep = getInitialMediaWorkflowStep(initialMedia);
    setMedia(initialMedia || null);
    setForm(mediaToForm(initialMedia, kind));
    setActiveStep(firstStep);
    setFurthestStep(firstStep);
    setErrors({});
    setTouched({});
    setMessage("");
    setMessageSeverity("info");
    setDirty(false);
    setDiscardOpen(false);
    setUploadStates({});
  }, [open, initialMedia, kind]);

  useEffect(() => {
    if (!open || !initialMediaId) return undefined;
    let active = true;
    mediaApi
      .get(initialMediaId)
      .then(({ media: refreshed }) => {
        if (!active) return;
        setMedia(refreshed);
        if (refreshed.assetStatus !== initialAssetStatus) {
          setMessageSeverity(refreshed.assetStatus === "failed" ? "error" : "success");
          setMessage(
            refreshed.assetStatus === "ready"
              ? "Video and poster are verified. This draft is ready to publish."
              : refreshed.assetError?.message || "The latest media status was loaded."
          );
        }
      })
      .catch((error) => {
        if (!active) return;
        setMessageSeverity("error");
        setMessage(error.message);
      });
    return () => {
      active = false;
    };
  }, [open, initialMediaId, initialAssetStatus]);

  const setValue = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };
      setErrors((currentErrors) => currentErrors[name]
        ? { ...currentErrors, [name]: validateMediaField(next, name, { mode: "continue" }) }
        : currentErrors);
      return next;
    });
    setDirty(true);
  };

  const blurField = (name) => {
    setTouched((current) => ({ ...current, [name]: true }));
    setErrors((current) => ({
      ...current,
      [name]: validateMediaField(form, name, { mode: "continue" }),
    }));
  };

  const focusFirstInvalidField = (validationErrors) => {
    const firstField = Object.keys(validationErrors)[0];
    if (!firstField) return;
    window.setTimeout(() => {
      const container = document.querySelector(`[data-media-field="${firstField}"]`);
      const target = container?.querySelector(
        "input, textarea, [contenteditable='true'], button, [tabindex]:not([tabindex='-1'])"
      ) || document.querySelector(`[data-media-field-input="${firstField}"]`);
      const scrollTarget = container || target;
      if (scrollTarget && typeof scrollTarget.scrollIntoView === "function") {
        scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (target && typeof target.focus === "function") target.focus();
    }, 0);
  };

  const saveDetails = async ({ nextStep = null, mode = "continue" } = {}) => {
    if (savingRef.current || actingRef.current) return null;
    const validationErrors = validateMediaForm(form, { mode });
    setErrors(validationErrors);
    setTouched((current) => ({
      ...current,
      ...Object.fromEntries(Object.keys(validationErrors).map((field) => [field, true])),
    }));
    if (Object.keys(validationErrors).length) {
      setMessageSeverity("error");
      setMessage(mode === "draft"
        ? "Add the minimum draft information shown below."
        : "Correct the highlighted fields before continuing.");
      focusFirstInvalidField(validationErrors);
      return null;
    }
    savingRef.current = true;
    setSaving(true);
    setMessage("");
    try {
      const payload = buildMediaPayload(form, kind);
      const result = media?._id
        ? await mediaApi.update(media._id, payload)
        : await mediaApi.create(payload);
      setMedia(result.media);
      setForm(mediaToForm(result.media, kind));
      setDirty(false);
      setMessageSeverity("success");
      setMessage(
        nextStep === 1
          ? "Draft saved. Add and verify the video and poster below."
          : "Changes saved successfully."
      );
      onSaved(result.media, { keepOpen: true });
      if (nextStep !== null) {
        setActiveStep(nextStep);
        setFurthestStep((current) => Math.max(current, nextStep));
      }
      return result.media;
    } catch (error) {
      setMessageSeverity("error");
      setMessage(error.message);
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const changeStatus = async (action) => {
    if (!media?._id || actingRef.current) return;
    actingRef.current = true;
    setActing(true);
    setMessage("");
    try {
      const result = await mediaApi[action](media._id);
      let savedMedia = result.media;
      if (action === "publish") {
        try {
          const refreshed = await mediaApi.get(media._id);
          savedMedia = refreshed.media;
        } catch {
          // The publish response is authoritative enough to confirm success;
          // a later list refresh will reconcile any additional server fields.
        }
      }
      setMedia(savedMedia);
      setForm(mediaToForm(savedMedia, kind));
      setDirty(false);
      setActiveStep(2);
      setFurthestStep(2);
      setMessageSeverity("success");
      setMessage(
        `Media ${action === "publish" ? "published successfully" : action === "unpublish" ? "returned to draft" : "archived"}.`
      );
      const shouldCloseAfterSuccess = action === "publish";
      onSaved(savedMedia, { keepOpen: !shouldCloseAfterSuccess });
      if (shouldCloseAfterSuccess) onClose();
    } catch (error) {
      setMessageSeverity("error");
      setMessage(error.message);
    } finally {
      actingRef.current = false;
      setActing(false);
    }
  };

  const updateVerifiedMedia = useCallback((nextMedia, { statusOnly = false } = {}) => {
    setMedia(nextMedia);
    const videoUploadActive = isAssetUploadActive(nextMedia, "video");
    const posterUploadActive = isAssetUploadActive(nextMedia, "poster");
    const replacementFailure = ["video", "poster"]
      .map((assetType) => nextMedia.assetUploads?.[assetType])
      .find((state) => state?.status === "failed");
    if (replacementFailure) {
      setMessageSeverity(nextMedia.video?.verifiedAt ? "warning" : "error");
      setMessage(
        replacementFailure.errorMessage ||
        (nextMedia.video?.verifiedAt
          ? "The replacement failed. Your previous media is still available."
          : "We couldn't prepare this media. Choose another file and try again.")
      );
    } else if (nextMedia.assetStatus === "failed") {
      setMessageSeverity("error");
      setMessage(nextMedia.assetError?.message || "Media processing failed.");
    } else if (videoUploadActive) {
      setMessageSeverity("info");
      setMessage("We're preparing your video. You can leave and return later; progress is saved.");
    } else if (posterUploadActive) {
      setMessageSeverity("info");
      setMessage("We're verifying your thumbnail. The current image remains available until it is ready.");
    } else {
      setMessageSeverity("success");
      setMessage("Verified media status refreshed from the backend.");
    }
    if (!statusOnly || ["ready", "failed"].includes(nextMedia.assetStatus) || replacementFailure) {
      onSaved(nextMedia, { keepOpen: true });
    }
  }, [onSaved]);

  const refreshStatus = async () => {
    if (!media?._id || refreshing) return;
    setRefreshing(true);
    try {
      const result = await mediaApi.get(media._id);
      updateVerifiedMedia(result.media, { statusOnly: true });
    } catch (error) {
      setMessageSeverity("error");
      setMessage(error.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleUploadState = useCallback((assetType, state) => {
    setUploadStates((current) => {
      const previous = current[assetType];
      if (
        previous?.busy === state.busy &&
        previous?.phase === state.phase &&
        previous?.processing === state.processing
      ) return current;
      return { ...current, [assetType]: state };
    });
  }, []);

  const hasActiveUpload = Object.values(uploadStates).some((state) => state?.busy);
  const readiness = useMemo(
    () => getPublicationReadiness(media, form, dirty),
    [dirty, form, media]
  );
  const channelError = errors.channels;

  const requestClose = () => {
    if (saving || acting || hasActiveUpload) return;
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  };

  const moveToStep = (step) => {
    if (step < 0 || step > furthestStep || saving || acting || hasActiveUpload) return;
    if (dirty && step !== 0) {
      setMessageSeverity("warning");
      setMessage("Save your detail changes before continuing.");
      return;
    }
    setActiveStep(step);
  };

  const continueToReview = () => {
    if (hasActiveUpload) return;
    setActiveStep(2);
    setFurthestStep(2);
    setMessage("");
  };

  const stepState = (index) => {
    if (index === activeStep) return "current";
    if (index > furthestStep) return "locked";
    if (index === 0 && media?._id && !dirty) return "complete";
    if (
      index === 1 &&
      media?.video?.verifiedAt &&
      media?.poster?.verifiedAt &&
      !isAssetUploadActive(media, "video") &&
      !isAssetUploadActive(media, "poster")
    ) return "complete";
    return "available";
  };

  const detailsStep = (
    <Box className="media-details-step">
      <Section title="Basic Information" description="Write the information people will see with this media.">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box data-media-field="title"><TextField required fullWidth label="Title" value={form.title}
              onChange={(event) => setValue("title", event.target.value)} error={Boolean(errors.title)}
              onBlur={() => blurField("title")}
              helperText={errors.title || "Use a clear, descriptive title."} /></Box>
          </Grid>
          <Grid item xs={12}>
            <RichTextField compact error={errors.summary} field="summary" label="Summary"
              required value={form.summary} onBlur={() => blurField("summary")}
              onChange={(value) => setValue("summary", value)} />
          </Grid>
          <Grid item xs={12}>
            <RichTextField error={errors.description} field="description" label="Description"
              onBlur={() => blurField("description")}
              value={form.description} onChange={(value) => setValue("description", value)} />
          </Grid>
          <Grid item xs={12}>
            <Box data-media-field="topic"><TextField required fullWidth label="Topic" value={form.topic}
              onChange={(event) => setValue("topic", event.target.value)} error={Boolean(errors.topic)}
              onBlur={() => blurField("topic")}
              helperText={errors.topic || "Required before publishing, for example Leadership or Communication."} /></Box>
          </Grid>
        </Grid>
      </Section>

      <Section
        title="Transcript"
        description="Add or edit the spoken content of this video. The transcript will appear as readable text on the published page."
      >
        <Box data-media-field="transcript" className="media-transcript-field">
          <TextField
            fullWidth
            multiline
            minRows={8}
            label="Transcript"
            value={form.transcript}
            onChange={(event) => setValue("transcript", event.target.value)}
            onBlur={() => blurField("transcript")}
            error={Boolean(errors.transcript)}
            helperText={errors.transcript || `${form.transcript.length.toLocaleString()} / ${TRANSCRIPT_MAX_LENGTH.toLocaleString()} characters · Optional`}
            inputProps={{ maxLength: TRANSCRIPT_MAX_LENGTH }}
          />
        </Box>
      </Section>

      <Section title="Classification" description="Choose where this post belongs in the LeaderBridge Ideas library, then add optional labels.">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required error={Boolean(errors.section)} data-media-field="section">
              <InputLabel id="media-section-label">Publishing Section</InputLabel>
              <Select
                labelId="media-section-label"
                label="Publishing Section"
                value={form.section}
                onChange={(event) => setValue("section", event.target.value)}
                onBlur={() => blurField("section")}
              >
                {EDITORIAL_SECTIONS.map((section) => (
                  <MenuItem key={section.value} value={section.value}>{section.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.section || "Choose Practice, Method, or Foundations for its location in LeaderBridge Ideas."}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            {listField("Categories", "categories", form.categories, (next) => setValue("categories", next), errors.categories, "Type a category and press Enter.", () => blurField("categories"))}
          </Grid>
          <Grid item xs={12} md={6}>
            {listField("Tags", "tags", form.tags, (next) => setValue("tags", next), errors.tags, "Type a tag and press Enter.", () => blurField("tags"))}
          </Grid>
        </Grid>
      </Section>

      <Section title="Publication settings" description="Choose the websites where this post should appear after publishing.">
        <Grid container spacing={2} alignItems="flex-start">
          {!form.channels.includes("leaderbridge") && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This {kind === "short" ? "Short" : "Video"} is not assigned to LeaderBridge, so it will not appear on leaderbridge.com/ideas. Select LeaderBridge below to publish it there.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControl data-media-field="channels" error={Boolean(channelError)} component="fieldset"
              onBlur={() => blurField("channels")}>
              <FormGroup row>
                {[["spaceas", "SPACEAS"], ["leaderbridge", "LeaderBridge"]].map(([value, label]) => (
                  <FormControlLabel key={value} control={<Checkbox checked={form.channels.includes(value)}
                    onChange={(event) => setValue("channels", event.target.checked
                      ? [...form.channels, value]
                      : form.channels.filter((channel) => channel !== value))} />} label={label} />
                ))}
              </FormGroup>
              <FormHelperText>{channelError || "Select LeaderBridge for the Ideas library; select SPACEAS only when this post should also appear there."}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className="media-featured-fields">
              <FormControlLabel control={<Switch checked={form.featured}
                onChange={(event) => setValue("featured", event.target.checked)} />} label="Featured" />
              <TextField type="number" label="Featured order" value={form.featuredOrder}
                onChange={(event) => setValue("featuredOrder", event.target.value)} disabled={!form.featured}
                error={Boolean(errors.featuredOrder)} helperText={errors.featuredOrder}
                inputProps={{ min: 0, step: 1 }} />
            </Box>
          </Grid>
        </Grid>
      </Section>

      <Section title="SEO & accessibility" description="Help people discover this idea and understand its thumbnail.">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="SEO title" value={form.seoTitle}
              onChange={(event) => setValue("seoTitle", event.target.value)} error={Boolean(errors.seoTitle)}
              helperText={errors.seoTitle || "Optional title for search results."} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={2} label="SEO description" value={form.seoDescription}
              onChange={(event) => setValue("seoDescription", event.target.value)} error={Boolean(errors.seoDescription)}
              helperText={errors.seoDescription || "Optional description for search engines."} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Poster alt text" value={form.posterAlt}
              onChange={(event) => setValue("posterAlt", event.target.value)} error={Boolean(errors.posterAlt)}
              helperText={errors.posterAlt || "Describe what appears in the thumbnail."} />
          </Grid>
        </Grid>
      </Section>
    </Box>
  );

  const uploadsStep = (
    <Box className="media-uploads-step">
      <Box className="media-step-introduction">
        <Box>
          <Typography variant="h6" component="h2">Upload and verify media</Typography>
          <Typography variant="body2" color="textSecondary">
            Add a video and thumbnail, preview them, and replace either one without losing your content details.
          </Typography>
        </Box>
        <Button size="small" startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={refreshStatus} disabled={refreshing || hasActiveUpload}>
          {refreshing ? "Refreshing…" : "Refresh status"}
        </Button>
      </Box>
      {isAssetUploadActive(media, "video") && (
        <Alert severity="info">We're preparing your video. You can safely close this window and return later.</Alert>
      )}
      <Grid container spacing={2} className="media-upload-grid">
        <Grid item xs={12} md={6}>
          <MediaUploadPanel assetType="video" media={media} onVerified={updateVerifiedMedia} onStateChange={handleUploadState} />
        </Grid>
        <Grid item xs={12} md={6}>
          <MediaUploadPanel assetType="poster" media={media} onVerified={updateVerifiedMedia} onStateChange={handleUploadState} />
        </Grid>
      </Grid>
    </Box>
  );

  const reviewStep = (
    <Box className="media-review-step">
      <Box className="media-review-heading">
        <Box>
          <Typography variant="h6" component="h2">Review before publishing</Typography>
          <Typography variant="body2" color="textSecondary">Confirm the saved details and backend-verified assets.</Typography>
        </Box>
        {media?._id && (
          <Button size="small" startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
            onClick={refreshStatus} disabled={refreshing || saving || acting}>
            {refreshing ? "Refreshing…" : "Refresh status"}
          </Button>
        )}
      </Box>

      <Box className="media-review-layout">
        <Section title="Content" description="Saved editorial details">
          <Box component="dl" className="media-review-values">
            <ReviewValue label="Title" fullWidth>{form.title}</ReviewValue>
            <ReviewRichText label="Summary" value={form.summary} />
            <ReviewRichText label="Description" value={form.description} />
            <ReviewTranscript value={form.transcript} />
            <ReviewValue label="Topic">{form.topic}</ReviewValue>
            <ReviewValue label="Type">{kind === "short" ? "Short" : "Video"}</ReviewValue>
            <ReviewValue label="Publishing section">{EDITORIAL_SECTIONS.find((item) => item.value === form.section)?.label}</ReviewValue>
            <ReviewValue label="Categories">{form.categories.join(", ")}</ReviewValue>
            <ReviewValue label="Tags">{form.tags.join(", ")}</ReviewValue>
          </Box>
          <Button size="small" onClick={() => moveToStep(0)} disabled={media?.status === "archived"}>Edit details</Button>
        </Section>

        <Section title="Media" description="Backend verification results">
          <Box className={`media-review-previews media-review-previews--${kind}`}>
            <Box className="media-review-preview-card">
              <Typography variant="subtitle2" component="h3">Video preview</Typography>
              <SecureMediaPreview assetType="video" media={media} />
            </Box>
            <Box className="media-review-preview-card">
              <Typography variant="subtitle2" component="h3">Poster preview</Typography>
              <SecureMediaPreview assetType="poster" media={media} />
            </Box>
          </Box>
          <Box component="dl" className="media-review-values">
            <ReviewValue label="Video">{media?.video?.verifiedAt ? "Verified" : media?.assetStatus === "processing" ? "Processing" : "Required"}</ReviewValue>
            <ReviewValue label="Poster">{media?.poster?.verifiedAt ? "Verified" : "Required"}</ReviewValue>
            <ReviewValue label="Duration">{formatDuration(media?.video?.durationSeconds)}</ReviewValue>
            <ReviewValue label="File size">{formatFileSize(media?.video?.sizeBytes)}</ReviewValue>
            <ReviewValue label="Resolution">{media?.video?.width && media?.video?.height ? `${media.video.width}×${media.video.height}` : "—"}</ReviewValue>
            <ReviewValue label="Orientation">{media?.video?.orientation}</ReviewValue>
            <ReviewValue label="Poster resolution">{media?.poster?.width && media?.poster?.height ? `${media.poster.width}×${media.poster.height}` : "—"}</ReviewValue>
            <ReviewValue label="Poster processing">{media?.poster?.conversionStatus === "converted" ? `Converted to 9:16 · ${media.poster.focalPoint || "center"} focus` : media?.poster?.verifiedAt ? "Verified without conversion" : "Required"}</ReviewValue>
            <ReviewValue label="Asset status"><span className={`media-status media-status--${media?.assetStatus}`}>{media?.assetStatus || "pending"}</span></ReviewValue>
          </Box>
          {media?.assetError?.message && <Alert severity="error">{media.assetError.message}</Alert>}
          <Button size="small" onClick={() => moveToStep(1)} disabled={media?.status === "archived"}>Manage media</Button>
        </Section>

        <Section title="Distribution" description="Where this media will appear after publishing">
          <Box component="dl" className="media-review-values">
            <ReviewValue label="Destinations">{form.channels.map((channel) => channel === "spaceas" ? "SPACEAS" : "LeaderBridge").join(", ")}</ReviewValue>
            <ReviewValue label="LeaderBridge Ideas">
              {form.channels.includes("leaderbridge")
                ? `${EDITORIAL_SECTIONS.find((item) => item.value === form.section)?.label || "Publishing section required"}`
                : "Not selected"}
            </ReviewValue>
            <ReviewValue label="Featured">{form.featured ? `Yes${form.featuredOrder !== "" ? ` · order ${form.featuredOrder}` : ""}` : "No"}</ReviewValue>
          </Box>
          {!form.channels.includes("leaderbridge") && (
            <Alert severity="warning">This post will not appear in LeaderBridge Ideas because LeaderBridge is not selected.</Alert>
          )}
        </Section>

        <Section title="SEO & Accessibility" description="Search metadata and poster description">
          <Box component="dl" className="media-review-values">
            <ReviewValue label="SEO title" fullWidth>{form.seoTitle}</ReviewValue>
            <ReviewValue label="SEO description" fullWidth>{form.seoDescription}</ReviewValue>
            <ReviewValue label="Poster alt text" fullWidth>{form.posterAlt}</ReviewValue>
          </Box>
        </Section>

        <Section
          title={readiness.canPublish ? "Ready to publish" : `${readiness.blockers.length || "Some"} ${readiness.blockers.length === 1 ? "thing needs" : "things need"} attention`}
          description={readiness.canPublish ? "Everything required has been saved and verified." : "Complete these items before publishing."}
        >
          <Box className="media-review-checklist">
            {readiness.steps.map((step) => (
              <Box key={step.key} className={`media-review-check media-review-check--${step.state}`}>
                <span>{readinessIcon(step.state)}</span>
                <Box><Typography variant="subtitle2">{step.label}</Typography><Typography variant="caption">{step.detail}</Typography></Box>
              </Box>
            ))}
          </Box>
          {!readiness.canPublish && readiness.blockers.length > 0 && (
            <Alert severity={media?.assetStatus === "failed" ? "error" : "info"}>
              <ul className="media-readiness-blockers">
                {readiness.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
              </ul>
            </Alert>
          )}
        </Section>
      </Box>
    </Box>
  );

  const footer = () => {
    if (activeStep === 0) {
      return (
        <>
          <Button onClick={requestClose} disabled={saving || acting}>Close</Button>
          {media?.status !== "published" && media?.status !== "archived" && (
            <Button variant="outlined" startIcon={<Save />}
              onClick={() => saveDetails({ mode: "draft" })}
              disabled={saving || acting}>
              Save Draft
            </Button>
          )}
          <Button variant="contained" startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <Save />}
            onClick={() => saveDetails({ nextStep: media?.status === "published" ? 2 : 1, mode: "continue" })}
            disabled={saving || acting || media?.status === "archived"}>
            {saving
              ? "Saving…"
              : media?.status === "published"
              ? "Save Changes & Review"
              : media?._id
              ? "Save Changes & Continue"
              : "Save Draft & Continue"}
          </Button>
        </>
      );
    }
    if (activeStep === 1) {
      return (
        <>
          <Button startIcon={<ArrowBack />} onClick={() => moveToStep(0)} disabled={hasActiveUpload}>Back to Details</Button>
          <Box className="media-editor-action-spacer" />
          <Button onClick={requestClose} disabled={hasActiveUpload}>Close</Button>
          <Button variant="contained" endIcon={<ArrowForward />} onClick={continueToReview} disabled={hasActiveUpload}>Continue to Review</Button>
        </>
      );
    }
    return (
      <>
        {media?.status !== "archived" && <Button startIcon={<ArrowBack />} onClick={() => moveToStep(1)} disabled={acting}>Back to Media</Button>}
        <Box className="media-editor-action-spacer" />
        <Button onClick={requestClose} disabled={acting}>Close</Button>
        {media?.status === "draft" && (
          <Button startIcon={<Save />} onClick={() => saveDetails()} disabled={saving || acting || !media?._id}>
            {saving ? "Saving…" : "Save Draft"}
          </Button>
        )}
        {media?.status === "published" && <Button onClick={() => changeStatus("unpublish")} disabled={acting}>Unpublish</Button>}
        {media && media.status !== "archived" && <Button color="warning" onClick={() => changeStatus("archive")} disabled={acting}>Archive</Button>}
        {media?.status === "draft" && (
          <Tooltip title={readiness.canPublish ? "Publish to the selected destinations" : readiness.blockers[0] || "Not ready to publish"}>
            <span>
              <Button color="success" variant="contained" startIcon={acting ? <CircularProgress size={17} color="inherit" /> : <Check />}
                onClick={() => changeStatus("publish")} disabled={!readiness.canPublish || acting || saving}>
                {acting ? "Publishing…" : "Publish"}
              </Button>
            </span>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={requestClose} fullScreen={fullScreen} fullWidth maxWidth="lg"
        className="media-editor-dialog" aria-labelledby="media-editor-title">
        <DialogTitle id="media-editor-title" className="media-wizard-title">
          <Box className="media-wizard-title-row">
            <Typography variant="h6" component="span">{media?._id ? "Edit" : "Create"} {kind === "short" ? "Short" : "Video"}</Typography>
            {media?.status && <span className={`media-status media-status--${media.status}`}>{media.status}</span>}
          </Box>
          <Box className="media-wizard-steps" aria-label="Creation progress">
            {WORKFLOW_STEPS.map((label, index) => {
              const state = stepState(index);
              return (
                <button type="button" key={label} className={`media-wizard-step media-wizard-step--${state}`}
                  onClick={() => moveToStep(index)} disabled={state === "locked" || saving || acting || hasActiveUpload}
                  aria-current={state === "current" ? "step" : undefined}>
                  <span className="media-wizard-step-number">{state === "complete" ? <Check fontSize="small" /> : index + 1}</span>
                  <span><strong>{label}</strong><small>{state === "current" ? "Current" : state === "complete" ? "Complete" : state === "locked" ? "Unavailable" : "Available"}</small></span>
                </button>
              );
            })}
          </Box>
        </DialogTitle>
        <DialogContent dividers className="media-wizard-content">
          {message && <Alert severity={messageSeverity} onClose={() => setMessage("")}>{message}</Alert>}
          {activeStep === 0 ? detailsStep : activeStep === 1 ? uploadsStep : reviewStep}
        </DialogContent>
        <DialogActions className="media-editor-actions">{footer()}</DialogActions>
      </Dialog>

      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} aria-labelledby="discard-media-title">
        <DialogTitle id="discard-media-title">Discard unsaved changes?</DialogTitle>
        <DialogContent><Typography>Your latest detail changes have not been saved.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={() => { setDiscardOpen(false); onClose(); }}>Discard changes</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MediaEditorDialog;
